"use client";

import { useAuth, useSignIn } from "@clerk/nextjs";
import { Lock } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import sarahAiLogo from "@/assets/sarah-ai-logo.png";
import { Button } from "@/components/ui/button";

export default function SignInPage() {
  const { signIn, isLoaded } = useSignIn();
  const { isSignedIn, signOut } = useAuth();
  const router = useRouter();

  // Redirect if already signed in
  useEffect(() => {
    if (!isLoaded) return;

    if (isSignedIn) {
      router.push("/companies");
    }
  }, [isLoaded, isSignedIn, router]);

  const handleGoogleSignIn = async () => {
    if (!isLoaded || !signIn) {
      return;
    }

    // If already signed in, sign out first to clear any stale state
    if (isSignedIn) {
      try {
        await signOut({ redirectUrl: window.location.href });
        // Wait a moment for sign out to complete and cookies to clear
        await new Promise((resolve) => setTimeout(resolve, 1000));
        // Clear local storage and session storage as well
        if (typeof window !== "undefined") {
          localStorage.clear();
          sessionStorage.clear();
        }
      } catch (error) {
        console.error("Error signing out:", error);
      }
    }

    try {
      await signIn.authenticateWithRedirect({
        strategy: "oauth_google" as const,
        redirectUrl: "/sso-callback",
        redirectUrlComplete: "/companies",
        oidcPrompt: "select_account", // Force Google to show account picker
      });
    } catch (error: unknown) {
      console.error("Error signing in with Google:", error);
      const errorObj = error as { status?: number; message?: string };

      // Handle "already signed in" error
      if (
        errorObj?.message?.includes("already signed in") ||
        errorObj?.message?.includes("You're already signed in")
      ) {
        // Try to sign out and retry
        try {
          await signOut();
          await new Promise((resolve) => setTimeout(resolve, 500));
          // Retry sign in
          await signIn.authenticateWithRedirect({
            strategy: "oauth_google" as const,
            redirectUrl: "/sso-callback",
            redirectUrlComplete: "/companies",
            oidcPrompt: "select_account", // Force Google to show account picker
          });
        } catch (retryError) {
          console.error("Error on retry:", retryError);
          alert(
            "Please clear your browser cookies and try again, or use a different browser window.",
          );
        }
        return;
      }

      if (
        errorObj?.status === 429 ||
        errorObj?.message?.includes("429") ||
        errorObj?.message?.includes("Too Many Requests")
      ) {
        alert("Too many sign-in attempts. Please wait a few minutes before trying again.");
        return;
      }
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      alert(`Error signing in: ${errorMessage}`);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Admin Branding */}
      <div className="flex-1 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center p-12">
        <div className="max-w-xl">
          <blockquote className="space-y-6">
            <p className="text-3xl font-light text-white leading-relaxed">
              &quot;Internal operations dashboard for Sarah platform administration. Manage
              companies, users, and system-wide operations with elevated access.&quot;
            </p>
            <footer className="text-slate-300 text-lg flex items-center gap-2">
              <Lock className="w-4 h-4" />— Restricted to authorized internal staff only
            </footer>
          </blockquote>
        </div>
      </div>

      {/* Right Side - Sign In */}
      <div className="flex-1 flex items-center justify-center p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-8">
              <Image
                src={sarahAiLogo}
                alt="Sarah AI"
                width={160}
                height={53}
                className="h-auto w-auto"
                priority
              />
            </div>
            <div className="mb-4">
              <h1 className="text-3xl font-semibold text-gray-900 mb-2">
                Sarah AI Internal Dashboard
              </h1>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                <Lock className="w-3 h-3" />
                Admin Access Required
              </div>
            </div>
            <p className="text-gray-600 text-lg">Sign in with your authorized Google account</p>
          </div>

          {!isLoaded ? (
            <Button
              disabled
              className="w-full h-12 text-base border border-gray-300 bg-white text-gray-700"
              variant="outline"
            >
              Loading...
            </Button>
          ) : (
            <Button
              onClick={handleGoogleSignIn}
              className="w-full h-12 text-base border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-3"
              variant="outline"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Sign in with Google</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
